import { AxiosStatic } from 'axios';
import { IBooklet } from '@/common/interfaces/magazine.interface';
import { IInterestingArticle } from '@/common/interfaces/interesting-for-you.interface';
import { IArticleCard } from '@/common/interfaces/article-card.interface';
import { IShippingCost } from '@/common/interfaces/shipping-info.interface';
import WpApiHelper from '@/helpers/WpApiHelper';
import { IPostId } from '@/common/interfaces/index.interface';
import { IMediaPost } from '@/common/interfaces/media.interface';
import { IPreviewBlogPost, ISliderBlogPost } from '@/common/interfaces/ringana-blog.interfaces';
import { IKnowledgeBaseOverview } from '@/common/interfaces/knowledge-base-overview.interface';
import ApiHelper from '../helpers/ApiHelper';
import ApiRoutes from '../api/ApiRoutes';
import { INewsEntry } from '../common/interfaces/news.interface';
import {
  IApiBooklet,
  IApiMediaPost,
  IApiRequestObject,
  IApiRethinkArticle,
  IApiSliderBlogPost,
  IApiPreviewBlogPost,
  IApiSingleProductData,
} from '../common/interfaces/api.interface';
import { IMenus } from '../common/interfaces/header.interface';
import { TApiResponse, TApiWpPages, TWpApiResponse } from '../common/types/api.types';
import WpApiRoutes from '../api/WpApiRoutes';
import config from '../../config';
import { ISingleProductData } from '../common/interfaces/products.interface';
import { ISummitDataWP } from '@/common/interfaces/summits.interface';
import { ISingleSummitWPData } from '@/common/interfaces/single-incentive.interface';

export default class WpApiService {
  private axios: AxiosStatic;

  constructor(axios: AxiosStatic) {
    this.axios = axios;
    this.axios.defaults.withCredentials = true;
  }

  private async fetch<T extends TWpApiResponse>(apiRoute: ApiRoutes, object: IApiRequestObject = {}): Promise<T> {
    let data;
    try {
      const requestConfig = ApiHelper.getRequestConfig(apiRoute, object);
      requestConfig.baseURL = config.wpUrls.restapi.baseurl;
      ({ data } = await this.axios.request(requestConfig));
    } catch (err) {
      ApiHelper.handleKnownApiError(err, apiRoute);
    }
    return data;
  }

  public async getInterestingForYou(options: IApiRequestObject): Promise<IInterestingArticle[]> {
    const response = await this.fetch<TApiResponse<IInterestingArticle[]>>(WpApiRoutes.getInterestingForYou, options);
    return response.data;
  }

  public async getBooklets(options: IApiRequestObject): Promise<IBooklet[]> {
    const response = await this.fetch<TApiResponse<IApiBooklet[]>>(WpApiRoutes.getBooklets, options);
    return WpApiHelper.transformIApiBookletsToIBooklets(response.data);
  }

  public async getKnowledgeBaseOverview(options: IApiRequestObject): Promise<{ data: IKnowledgeBaseOverview; success: boolean }> {
    const response = await this.fetch<TApiResponse<IKnowledgeBaseOverview>>(WpApiRoutes.getKnowledgeBaseOverview, options);
    return {
      data: response.data,
      success: response.success as boolean,
    };
  }

  public async getMedia(options: IApiRequestObject): Promise<{ count: number; data: IMediaPost[] }> {
    const response = await this.fetch<TApiResponse<IApiMediaPost[]>>(WpApiRoutes.getMedia, options);

    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiMediaPostsToIMediaPosts(response.data),
    };
  }

  public async getNews(options: IApiRequestObject): Promise<INewsEntry[]> {
    const response = await this.fetch<TApiResponse<INewsEntry[]>>(WpApiRoutes.getNews, options);
    return response.data;
  }

  public async getShippingInfo(options: IApiRequestObject): Promise<IShippingCost[]> {
    const response = await this.fetch<TApiResponse<IShippingCost[]>>(WpApiRoutes.getShippingInfo, options);
    return response.data;
  }

  public async getRethinkArticles(options: IApiRequestObject): Promise<IArticleCard[]> {
    const response = await this.fetch<IApiRethinkArticle[]>(WpApiRoutes.getRethinkArticles, options);
    return WpApiHelper.transformIApiRethinkArticlesToIArticleCards(response);
  }

  public async getWpPageUrls(options: IApiRequestObject): Promise<TApiWpPages> {
    return this.fetch(WpApiRoutes.getUrls, options);
  }

  public async getWpPostId(options: IApiRequestObject): Promise<IPostId> {
    const response = await this.fetch<TApiResponse<IPostId>>(WpApiRoutes.getPostId, options);
    return response.data;
  }

  public async getSliderBlogPosts(options: IApiRequestObject): Promise<{count: number; data: ISliderBlogPost[]}> {
    const response = await this.fetch<TApiResponse<IApiSliderBlogPost[]>>(WpApiRoutes.getSliderPosts, options);
    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiSliderBlogPostsToISliderBlogPosts(response.data),
    };
  }

  public async getPreviewBlogPosts(options: IApiRequestObject): Promise<{count: number; data: IPreviewBlogPost[]}> {
    const response = await this.fetch<TApiResponse<IApiPreviewBlogPost[]>>(WpApiRoutes.getPreviewPosts, options);
    return {
      count: response.count as number,
      data: WpApiHelper.transformIApiPreviewBlogPostsToIPreviewBlogPosts(response.data),
    };
  }

  public async getWpMenuItems(options: IApiRequestObject): Promise<IMenus> {
    const requestOptions = options;
    requestOptions.axiosConfig = {
      baseURL: `/`,
    };

    return this.fetch<IMenus>(WpApiRoutes.getMenuItems, requestOptions);
  }

  public async getWpMenuItemsForPartners(options: IApiRequestObject): Promise<IMenus> {
    const requestOptions = options;
    requestOptions.axiosConfig = {
      baseURL: `/`,
    };

    return this.fetch<IMenus>(WpApiRoutes.getMenuItemsForPartners, requestOptions);
  }

  public async getSingleProductData(options: IApiRequestObject, imagesBaseUrl: string): Promise<ISingleProductData> {
    const response = await this.fetch<IApiSingleProductData[]>(WpApiRoutes.getSingleProductData, options);
    return WpApiHelper.transformIApiSingleProductDataToISingleProductData(response[0], imagesBaseUrl);
  }

  public async getSummits(): Promise<ISummitDataWP[]> {
    const response = await this.fetch<TApiResponse<ISummitDataWP[]>>(WpApiRoutes.getSummits);
    return response.data;
  }

  public async getSingleSummitData(options: IApiRequestObject): Promise<ISingleSummitWPData> {
    const response = await this.fetch<TApiResponse<ISingleSummitWPData>>(WpApiRoutes.getSingleSummitData, options);
    return response.data;
  }
}
